<template>
  <!--
    One container, so the chips are spaced by this component rather than by whatever gutter the
    call site happens to put it in. Those are q-col-gutter-xs, 4px, where M3 spaces a chip set by
    8dp; the chips used to make up the difference with a margin each, which is not theirs to carry.
  -->
  <div class="filter-chips-bar">
    <div
      v-for="(filter, filterKey) in filters"
      :key="filterKey"
      class="filter-chips-bar__group"
    >
      <hr-input-chip
        v-for="(filterStatement, idx) in (filter?.filterStatements ?? [])"
        :key="`${filterKey}-${idx}`"
        :size="size"
        :label="resolveLabel(filterStatement, filterKey)"
        :hide-remove="!isRemovable(filterKey)"
        :remove-aria-label="i18n.t('common.filter.buttonRemoveFilterLabel', {
          filter: resolveLabel(filterStatement, filterKey),
        })"
        :disable="!isEditable(filterKey)"
        clickable
        class="filter-chips-bar__chip"
        @click="openDialog(filterKey)"
        @remove="onRemove(filterKey, idx)"
      />
    </div>
    <div
      v-if="!hideAdd"
      class="filter-chips-bar__group"
    >
      <hr-assist-chip
        :size="size"
        :label="addLabel ?? i18n.t('common.buttonAddFilterLabel')"
        :disable="disableAdd"
        :legacy-props="LEGACY_ADD_CHIP_PROPS"
        icon="add"
        data-e2e="chip-add-filter"
        class="filter-chips-bar__chip"
        @click="openDialog(null)"
      />
    </div>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { computed } from 'vue';

import { removeFilterStatement } from 'components/UI/filterDialog/filterMap';
import { getMandatoryFilterKeys } from 'components/UI/filterDialog/mandatoryFilters';
import createFilterDialogOpener from 'components/UI/filterDialog/openFilterDialog';
import { HrAssistChip, HrInputChip } from 'src/design-system';

/**
 * The filters applied to a table: the chips that show them, and the dialog that edits them.
 *
 * Both halves live here because they are two views of the same data. A caller binds the filter map
 * and hears about every change through one event, rather than opening the dialog itself and
 * reassembling the result a filter at a time.
 *
 * @typedef {{ label: string }} FilterStatement
 * @typedef {Record<string, { filterStatements: FilterStatement[], isPersistent?: boolean }>} Filters
 */

defineOptions({ name: 'FilterChipsBar' });

/*
 * The design system chips carry their own legacy palette (grey for input, plain outline for
 * assist). Filter chips have always been primary/white, so pin that for users still on the
 * legacy theme. Both drop out automatically under a light or dark theme, and this whole block
 * goes when the legacy theme does.
 */
const LEGACY_ADD_CHIP_PROPS = { color: 'primary' };

const props = defineProps({
  /** @type {import('vue').PropType<Filters>} The filters currently applied. Use with v-model. */
  filters: {
    type: Object,
    required: true,
  },

  /** Every criterion the user may filter on. */
  filterCriteria: {
    type: Array,
    required: true,
  },

  /** Group labels the caller declared. No group structure is invented from them. */
  filterGroups: {
    type: Array,
    default: () => ([]),
  },

  /**
   * Criteria the user may re-point but never drop, because the filter bounds an otherwise
   * unbounded read. A filter carrying isPersistent is treated the same way; the difference is
   * only whether the caller knows the key up front or the filter carries the flag.
   */
  lockedFilterKeys: {
    type: Array,
    default: () => ([]),
  },

  /**
   * Criteria the user may not change at all, which also cannot be removed and are not offered in
   * the dialog. For a view pinned to one record, the filters naming that record are not a bound to
   * adjust; they are what the view is.
   */
  readonlyFilterKeys: {
    type: Array,
    default: () => ([]),
  },

  size: {
    type: String,
    default: 'md',
  },

  /** Falls back to the shared "Add filter" translation. */
  addLabel: {
    type: String,
    default: null,
  },

  /** Drops the trailing "add filter" chip. */
  hideAdd: {
    type: Boolean,
    default: false,
  },

  /** Keeps the "add filter" chip visible but inert. */
  disableAdd: {
    type: Boolean,
    default: false,
  },

  /** Drops the remove button from every chip. */
  hideRemove: {
    type: Boolean,
    default: false,
  },

  /** Dims every chip and blocks the edit click. */
  disableEdit: {
    type: Boolean,
    default: false,
  },

  /**
   * Overrides the chip text, for filters whose label is derived rather than stored.
   *
   * A function rather than a slot: the text has to reach the chip's `label` prop, which is
   * what wraps it in the `.ellipsis` div that makes `max-width` truncate.
   * @type {import('vue').PropType<(s: FilterStatement, k: string) => string>}
   */
  getLabel: {
    type: Function,
    default: null,
  },

  /** Overrides the clients the dialog's picker offers. */
  customClients: {
    type: Array,
    default: null,
  },

  /** Overrides the users the dialog's picker offers. */
  customUsers: {
    type: Array,
    default: null,
  },

  /** Replaces the dialog's own title on its criteria list. */
  customTitle: {
    type: String,
    default: null,
  },

  /**
   * Awaited before the dialog opens, for a caller whose criteria depend on data it loads lazily.
   * The session activities table reads its note fields this way rather than on every page load.
   */
  beforeOpen: {
    type: Function,
    default: null,
  },
});

/*
 * The second argument says how the change was made. A caller that only binds the map ignores it,
 * including v-model; it is there for the one measuring whether a criterion was dropped from its
 * chip or by being replaced in the dialog, which is a distinction its analytics already draw.
 */
const CHANGED_VIA = { CHIP: 'chip', DIALOG: 'dialog' };

const emit = defineEmits({
  /** The whole map after the change, plus 'chip' or 'dialog' as its source. */
  'update:filters': null,
});

/*
 * Everything protected from removal: keys the caller named, filters carrying isPersistent, and
 * the read-only ones, which are unremovable by definition.
 */
const mandatoryKeys = computed(() => getMandatoryFilterKeys(
  props.filters,
  [...props.lockedFilterKeys, ...props.readonlyFilterKeys],
));

const isEditable = (filterKey) => !props.disableEdit
  && !props.readonlyFilterKeys.includes(filterKey);

const isRemovable = (filterKey) => !props.hideRemove
  && isEditable(filterKey)
  && !mandatoryKeys.value.has(filterKey);

function resolveLabel(filterStatement, filterKey) {
  return props.getLabel === null
    ? filterStatement.label
    : props.getLabel(filterStatement, filterKey);
}

/* A read-only criterion is not offered in the dialog either, not merely dimmed on its chip. */
const editableCriteria = computed(() => props.filterCriteria
  .filter((criterion) => !props.readonlyFilterKeys.includes(criterion.value)));

const openFilterDialog = createFilterDialogOpener(() => ({
  filters: props.filters,
  filterCriteria: editableCriteria.value,
  filterGroups: props.filterGroups,
  lockedFilterKeys: [...props.lockedFilterKeys, ...props.readonlyFilterKeys],
  customClients: props.customClients,
  customUsers: props.customUsers,
  customTitle: props.customTitle,
  beforeOpen: props.beforeOpen,
  onApply: (filters) => emit('update:filters', filters, CHANGED_VIA.DIALOG),
}));

/* A read-only criterion is not offered in the dialog, so its chip does not open one either. */
function openDialog(openAt) {
  if (openAt !== null && !isEditable(openAt)) {
    return;
  }

  openFilterDialog(openAt);
}

/*
 * A chip stands for one statement, so its cross takes that statement alone. A match-all filter
 * renders one chip per value, and removing all of them from one cross would drop values the user
 * never pointed at.
 */
function onRemove(filterKey, statementIndex) {
  emit(
    'update:filters',
    removeFilterStatement(filterKey, statementIndex, props.filters),
    CHANGED_VIA.CHIP,
  );
}
</script>

<style lang="scss" scoped>
/*
 * The same 8px between every chip, whether the two sit in one filter or in neighbouring ones: a
 * reader sees one set of chips, not a set of sets. The group exists to keep the several chips of a
 * match-all filter together, not to space them differently.
 */
.filter-chips-bar,
.filter-chips-bar__group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

/*
 * Collapses the three max-widths the call sites had grown (350px, 250px, and an
 * accidental 250px leaking from Scheduling's unscoped style block). 350px is the
 * widest of them, so no filter label truncates more than it did before. Truncation
 * itself is Quasar's: it wraps the `label` prop in a .ellipsis div, so bounding the
 * chip is enough.
 *
 * The cap has to be a plain px value, not min(350px, 100%): a percentage max-width
 * is cyclic against this group, whose own width comes from its content, so the
 * browser ignores it when sizing the group and the group ends up as wide as the
 * unbounded label — leaving a gap before the next group that looks like broken chip
 * spacing. max-width: 100% on the group itself restores the "never wider than the
 * toolbar" cap that percentage was there for.
 *
 * min-width: 0 on the group is what lets a chip shrink at all: .q-chip__content is
 * white-space: nowrap, so a flex item's min-content is otherwise the whole unbroken
 * label, and min-width: auto pins it there however little room the row has left.
 * min-width: 0 on the chip does the same one level down, inside the group.
 *
 * font-weight is pinned because two call sites sit under a bold ancestor and used
 * to correct for it locally.
 *
 * Scoped off the group wrapper rather than the chip itself: the wrapper is rendered
 * here so it always carries the scope id, whereas the chip is three components down
 * and would depend on Vue propagating the id through each nested root.
 */
.filter-chips-bar__group {
  min-width: 0;
  max-width: 100%;
}

.filter-chips-bar__group :deep(.filter-chips-bar__chip) {
  max-width: 350px;
  min-width: 0;
  font-weight: normal;
}
</style>
